import { useState, useEffect, useCallback } from 'react';

/**
 * Photo processing queue hook
 * Manages async processing of photos without blocking UI (parallel mode)
 *
 * @param {Function} processFunction - Async function to process each photo
 * @returns {Object} Queue management functions and state
 */
export function usePhotoQueue(processFunction) {
  const [queue, setQueue] = useState([]);
  const [processingItems, setProcessingItems] = useState(new Set());

  /**
   * Add item to queue
   * @param {Object} item - Item to process (must have unique `id`)
   */
  const enqueue = useCallback((item) => {
    if (!item || !item.id) {
      console.error('[usePhotoQueue] Item must have an id');
      return;
    }

    setQueue(prev => [...prev, item]);
  }, []);

  /**
   * Process all queued items in parallel
   */
  useEffect(() => {
    if (queue.length === 0) return;

    // Start processing all items in queue
    queue.forEach(item => {
      setProcessingItems(prev => new Set(prev).add(item.id));

      (async () => {
        try {
          await processFunction(item);
        } catch (err) {
          console.error('[usePhotoQueue] Processing failed:', err);
        } finally {
          setProcessingItems(prev => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
        }
      })();
    });

    // Clear queue since all items are now processing
    setQueue([]);
  }, [queue, processFunction]);

  /**
   * Clear entire queue
   */
  const clear = useCallback(() => {
    setQueue([]);
    setProcessingItems(new Set());
  }, []);

  return {
    enqueue,
    clear,
    queueLength: queue.length,
    isProcessing: processingItems.size > 0,
    processingCount: processingItems.size,
    // For display: total pending (queue + processing)
    pendingCount: queue.length + processingItems.size
  };
}
